import { Box, Button, Card, CardContent, CircularProgress, Grid, Snackbar, TextField } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { AlertTitle } from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';
import { Auth } from 'aws-amplify';
import { AuthPiece } from 'aws-amplify-react';
import { useState } from 'react';
import InputMask from 'react-input-mask';
import useDebouncedEffect from 'use-debounced-effect';

import { JoiAuthForgotPasswordSubmit } from './custom.auth.joi';
import { useAuthStyles } from './styles';
import { useFormFields } from '../../../lib/hooks.lib';
import { ApplicationFormContainer } from '../../../private/pages/ApplicationsForm/applications-form.styles';

export const CustomForgotPassword = (props) => {
  const classes = useAuthStyles();
  const authPiece = new AuthPiece(props);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sendView, setSendView] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [fields, handleFieldChange] = useFormFields({
    username: '',
    code: '',
    password: ''
  });

  useDebouncedEffect(
    () => {
      if (fields.password && confirmPassword) {
        if (fields.password === confirmPassword) {
          setPasswordsMatch(true);
        } else {
          setPasswordsMatch(false);
        }
      } else {
        setPasswordsMatch(true);
      }
    },
    750,
    [fields.password, confirmPassword]
  );

  const checkUsernameFilled = () => {
    return fields.username.length;
  };

  const checkFormFilled = () => {
    return Object.values(fields).every(Boolean) && passwordsMatch;
  };

  const toErrorMessages = (error: unknown): string[] => {
    if (error instanceof Error) {
      if (Array.isArray((error as Error & { message: unknown }).message)) {
        return (error as Error & { message: string[] }).message;
      }

      return [error.message];
    }

    return [String(error)];
  };

  const handleForgotPasswordSend = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await Auth.forgotPassword(fields.username);
      setLoading(false);
      setSendView(false);
    } catch (error) {
      console.log(error);
      setErrorMessages(toErrorMessages(error));
      setLoading(false);
    }
  };

  const handleForgotPasswordResend = async (event) => {
    event.preventDefault();
    try {
      await Auth.forgotPassword(fields.username);
      setSnackBarOpen(true);
    } catch (error) {
      console.log(error);
      setErrorMessages(toErrorMessages(error));
    }
  };

  const validateForm = async (): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const validFields = await JoiAuthForgotPasswordSubmit.validateAsync(fields);

        return resolve(validFields);
      } catch (error) {
        return reject(toErrorMessages(error));
      }
    });
  };

  const handleForgotPasswordSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await validateForm();
      await Auth.forgotPasswordSubmit(data.username, data.code, data.password);
      setLoading(false);
      authPiece.changeState('signIn');
      setErrorMessages([]);
    } catch (error) {
      console.log(error);
      const parsedMessages = Array.isArray(error) ? (error as string[]) : toErrorMessages(error);
      setErrorMessages(parsedMessages);
      setLoading(false);
    }
  };

  const renderForgotPasswordSend = () => (
    <Box pt={3} mb={3}>
      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={10} sm={5}>
          <Card>
            <CardContent>
              <form>
                <Grid container justifyContent="space-between" spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      id="username"
                      name="username"
                      label="Username"
                      type="text"
                      fullWidth
                      value={fields.username}
                      onChange={handleFieldChange}
                    />
                  </Grid>
                </Grid>
                <Grid container justifyContent="center">
                  <Grid item style={{ marginTop: '20px' }}>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      className={classes.button}
                      disabled={!checkUsernameFilled()}
                      onClick={(e) => handleForgotPasswordSend(e)}
                    >
                      Send
                      {loading && (
                        <Box ml={2} mt={1}>
                          <CircularProgress size="0.8rem" color={'inherit'} />
                        </Box>
                      )}
                    </Button>
                  </Grid>
                </Grid>
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    authPiece.changeState('signIn');
                    setErrorMessages([]);
                  }}
                  variant="body2"
                >
                  Back to sign in
                </Link>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderForgotPasswordSubmit = () => (
    <Box pt={3} mb={3}>
      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={5}>
          <Card>
            <CardContent>
              <form>
                <Grid container justifyContent="space-between" spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      id="username"
                      name="username"
                      label="Username"
                      type="text"
                      fullWidth
                      value={fields.username}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputMask mask="999999" disabled={false} maskChar={null} value={fields.code} onChange={handleFieldChange}>
                      {() => <TextField required id="code" name="code" label="Code" type="code" fullWidth />}
                    </InputMask>
                    <Link component="button" onClick={(e) => handleForgotPasswordResend(e)} variant="body2" style={{ float: 'right' }}>
                      Resend code
                    </Link>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      error={!passwordsMatch}
                      helperText="At least 8 characters with mix of upper and lowercase letters, numbers and special characters."
                      id="password"
                      name="password"
                      label="Password"
                      type="password"
                      fullWidth
                      value={fields.password}
                      onChange={handleFieldChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      error={!passwordsMatch}
                      helperText={!passwordsMatch && 'Passwords do not match.'}
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm Password"
                      type="password"
                      fullWidth
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container justifyContent="center">
                  <Grid item style={{ marginTop: '20px' }}>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={!checkFormFilled()}
                      className={classes.button}
                      onClick={(e) => handleForgotPasswordSubmit(e)}
                    >
                      Reset Password
                      {loading && (
                        <Box ml={2} mt={1}>
                          <CircularProgress size="0.8rem" color={'inherit'} />
                        </Box>
                      )}
                    </Button>
                  </Grid>
                </Grid>
                <Link component="button" type="button" onClick={() => authPiece.changeState('signIn')} variant="body2">
                  Back to sign in
                </Link>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderForgotPassword = () => (
    <ApplicationFormContainer>
      <Box ml={4} mr={4} mb={3}>
        {errorMessages.length > 0 && (
          <Alert
            severity="error"
            className={classes.alert}
            onClose={() => {
              setErrorMessages([]);
            }}
          >
            <AlertTitle>Validation Errors</AlertTitle>
            <ul>
              {errorMessages.map((item, i) => {
                return <li key={i}>{item}</li>;
              })}
            </ul>
          </Alert>
        )}
        <Grid container justifyContent="center">
          <Grid item>
            <Typography variant="h4">Reset Password</Typography>
            <Typography variant="body1">
              Enter your username below, then confirm the new password with the 6-digit code sent to your phone.
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {sendView ? renderForgotPasswordSend() : renderForgotPasswordSubmit()}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBarOpen}
        onClose={() => setSnackBarOpen(false)}
        message="Code resent"
        autoHideDuration={2000}
      />
    </ApplicationFormContainer>
  );

  return <div>{props.authState === 'forgotPassword' && renderForgotPassword()}</div>;
};
